import React, { useEffect, useState } from 'react';

export default function NurseIncidents({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [camperFirst, setCamperFirst] = useState('');
  const [camperLast, setCamperLast] = useState('');
  const [dob, setDob] = useState('');
  const [incidentAt, setIncidentAt] = useState('');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState('Injury');
  const [description, setDescription] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [guardianNotified, setGuardianNotified] = useState(false);
  const [guardianNotifiedAt, setGuardianNotifiedAt] = useState('');
  const [notes, setNotes] = useState('');

  const [openId, setOpenId] = useState('');

  const fetchIncidents = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/.netlify/functions/admin-get-incidents', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw };
      }
      if (!res.ok) {
        setError(json?.error || `Failed to load: ${res.status}`);
        setRows([]);
        return;
      }
      setRows(Array.isArray(json.rows) ? json.rows : []);
    } catch (e) {
      setError('Network error while loading incidents');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchIncidents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const addIncident = async () => {
    setError('');

    if (!camperFirst.trim() || !camperLast.trim() || !dob.trim()) {
      setError('Camper first, last, and DOB are required.');
      return;
    }

    const payload = {
      camperFirst: camperFirst.trim(),
      camperLast: camperLast.trim(),
      dob: dob.trim(),
      incidenteAt: incidentAt ? new Date(incidentAt).toISOString() : new Date().toISOString(),
      location,
      category,
      description,
      actionTaken,
      guardianNotified,
      guardianNotifiedAt: guardianNotifiedAt ? new Date(guardianNotifiedAt).toISOString() : '',
      notes
    };

    try {
      const res = await fetch('/.netlify/functions/admin-add-incident', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw };
      }
      if (!res.ok) {
        setError(json?.error || `Failed to add: ${res.status}`);
        return;
      }

      setCamperFirst('');
      setCamperLast('');
      setDob('');
      setIncidentAt('');
      setLocation('');
      setCategory('Injury');
      setDescription('');
      setActionTaken('');
      setGuardianNotified(false);
      setGuardianNotifiedAt('');
      setNotes('');

      await fetchIncidents();
    } catch (e) {
      setError('Network error while adding incident');
    }
  };

  const fmtWhen = (r) => {
    const v = r.incidenteAt || r.incidentAt || r.createdAt || r.created_at || '';
    const t = Date.parse(v);
    if (!t) return '—';
    return new Date(t).toLocaleString();
    };

  const guardianLabel = (r) => {
    const gn = String(r.guardianNotified || '').toLowerCase();
    return gn === 'true' || gn === 'yes' || gn === '1' ? 'Yes' : 'No';
  };

  const rowKey = (r) => String(r.id || `${r.camperFirst || ''}-${r.camperLast || ''}-${r.dob || ''}-${r.createdAt || ''}`);

  const toggleOpen = (id) => setOpenId((prev) => (prev === id ? '' : id));

  return (
    <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <div>
            <h2 style={{ margin: 0 }}>Incidents</h2>
            <div className="muted" style={{ marginTop: 4 }}>
              Record injuries/illness and actions taken. Guardian notification is optional.
            </div>
          </div>
        </div>

        {error ? <div className="error" style={{ marginTop: 10 }}>{error}</div> : null}

        {/* Form */}
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
          <input value={camperFirst} onChange={(e) => setCamperFirst(e.target.value)} placeholder="Camper first name" style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }} />
          <input value={camperLast} onChange={(e) => setCamperLast(e.target.value)} placeholder="Camper last name" style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }} />
          <input value={dob} onChange={(e) => setDob(e.target.value)} placeholder="DOB (e.g. 7/9/2024)" style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }} />
          <input value={incidentAt} onChange={(e) => setIncidentAt(e.target.value)} placeholder="Incident time (optional)" type="datetime-local" style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }} />
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location (optional)" style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }} />

          <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd', background: '#fff' }}>
            <option value="Injury">Injury</option>
            <option value="Illness">Illness</option>
            <option value="Medication">Medication</option>
            <option value="Allergy">Allergy</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" rows={3} style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }} />
          <textarea value={actionTaken} onChange={(e) => setActionTaken(e.target.value)} placeholder="Action taken" rows={2} style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }} />

          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={guardianNotified} onChange={(e) => setGuardianNotified(e.target.checked)} />
            Parent/guardian notified (optional)
          </label>

          {guardianNotified ? (
            <input
              value={guardianNotifiedAt}
              onChange={(e) => setGuardianNotifiedAt(e.target.value)}
              type="datetime-local"
              placeholder="Notified at"
              style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }}
            />
          ) : null}

          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional notes (optional)" rows={2} style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #ddd' }} />

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn primary" onClick={addIncident}>
              Add incident
            </button>
          </div>
        </div>
      </div>

      {/* Recent incidents: compact table + view */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Recent incidents</h3>
        {loading ? <div>Loading…</div> : null}
        {!loading && !rows.length ? <div className="muted">No incidents yet.</div> : null}

        {!loading && rows.length ? (
          <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Camper', 'Incident date', 'Category', 'Guardian notified', ''].map((h) => (
                    <th
                      key={h || 'actions'}
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderBottom: '1px solid #eee',
                        fontSize: 12,
                        color: '#666',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.slice(0, 50).map((r) => {
                  const id = rowKey(r);
                  const camper = `${r.camperFirst || ''} ${r.camperLast || ''}`.trim() || '—';
                  const isOpen = openId === id;

                  return (
                    <React.Fragment key={id}>
                      <tr>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f1f1', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {camper}
                        </td>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f1f1', whiteSpace: 'nowrap' }}>
                          {fmtWhen(r)}
                        </td>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f1f1' }}>
                          {r.category || '—'}
                        </td>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f1f1' }}>
                          {guardianLabel(r)}
                        </td>
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid #f1f1f1' }}>
                          <button className="btn" type="button" onClick={() => toggleOpen(id)}>
                            {isOpen ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>

                      {isOpen ? (
                        <tr>
                          <td colSpan={5} style={{ padding: '10px 10px', borderBottom: '1px solid #f1f1f1', background: '#fafafa' }}>
                            <div style={{ display: 'grid', gap: 6 }}>
                              <div><strong>DOB:</strong> {r.dob || '—'}</div>
                              <div><strong>Location:</strong> {r.location || '—'}</div>
                              <div style={{ whiteSpace: 'pre-wrap' }}><strong>Description:</strong> {r.description || '—'}</div>
                              <div style={{ whiteSpace: 'pre-wrap' }}><strong>Action taken:</strong> {r.actionTaken || '—'}</div>
                              <div><strong>Guardian notified at:</strong> {r.guardianNotifiedAt ? new Date(r.guardianNotifiedAt).toLocaleString() : '—'}</div>
                              <div style={{ whiteSpace: 'pre-wrap' }}><strong>Notes:</strong> {r.notes || '—'}</div>
                              <div className="muted" style={{ fontSize: 12 }}>
                                Created: {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
                                {r.createdBy ? ` • by ${r.createdBy}` : ''}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {rows.length > 50 ? (
              <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                Showing newest 50.
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}